import { Link } from 'react-router-dom'

type Section = {
  heading: string
  body: string
}

type StaticPageContent = {
  title: string
  intro: string
  sections: Section[]
}

export const staticPages: Record<string, StaticPageContent> = {
  about: {
    title: '关于本站',
    intro: 'Parentool 是面向普通办公用户的在线工具站，重点提供 PDF、图片、文本和常用开发调试工具。',
    sections: [
      {
        heading: '我们提供什么',
        body: '本站优先建设日常办公高频工具，例如 PDF 压缩、PDF 合并拆分、图片压缩、图片加水印、证件照裁剪、文本处理和跨设备剪切板。'
      },
      {
        heading: '处理原则',
        body: '文件类工具尽量在浏览器本地完成处理，减少上传文件的需要。需要服务器配合的功能会在工具页说明，例如跨设备剪切板只保存临时文本。'
      },
      {
        heading: '持续改进',
        body: '如果某个工具失败、效果不符合预期或你希望增加新功能，可以通过联系我们页面反馈。'
      }
    ]
  },
  privacy: {
    title: '隐私政策',
    intro: '我们尽量减少数据收集，并把文件处理放在你的设备本地完成。',
    sections: [
      {
        heading: '本地处理',
        body: '文件类工具优先在你的浏览器本地处理，例如图片压缩、图片裁剪、PDF 合并拆分和文本格式化通常不会把原始文件上传到服务器。'
      },
      {
        heading: '跨设备剪切板',
        body: '跨设备剪切板需要服务器临时保存你主动提交的文本内容，并按你选择的过期时间清理。请不要存放密码、验证码、证件号码等敏感内容。'
      },
      {
        heading: '基础日志',
        body: '服务器可能记录访问时间、请求路径、IP、浏览器信息等基础日志，用于排查故障、安全防护和统计站点运行状态。'
      }
    ]
  },
  contact: {
    title: '联系我们',
    intro: '欢迎反馈工具问题、功能建议、错别字和合作相关事项。',
    sections: [
      {
        heading: '反馈方式',
        body: '你可以发送邮件到 contact@parentool.cn。反馈问题时建议附上工具名称、浏览器、操作步骤和报错截图。'
      },
      {
        heading: '功能建议',
        body: '本站会优先处理普通办公用户常见需求，包括 PDF、图片、文本、跨设备共享和简单格式转换。'
      }
    ]
  },
  disclaimer: {
    title: '免责声明',
    intro: '本站工具用于提升日常办公效率，处理结果需要你自行核对。',
    sections: [
      {
        heading: '工具结果',
        body: 'PDF、图片、文本和格式转换工具的输出可能受浏览器、文件格式、文件损坏或第三方库限制影响。重要文件请先保留原件。'
      },
      {
        heading: '专业建议',
        body: '健康、成长评估、投资相关页面只提供一般信息或辅助计算，不构成医疗、法律、投资或其他专业建议。'
      },
      {
        heading: '外部链接',
        body: '本站可能链接到外部资源。访问外部网站时，请自行判断其内容、隐私政策和安全性。'
      }
    ]
  }
}

type StaticPageProps = {
  pageId: keyof typeof staticPages
}

const StaticPage = ({ pageId }: StaticPageProps) => {
  const page = staticPages[pageId]

  return (
    <div className="bg-slate-50">
      <article className="mx-auto max-w-4xl px-4 py-10 md:py-14">
        <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-950">
          返回首页
        </Link>
        <header className="mt-5 border-b border-slate-200 pb-6">
          <h1 className="text-3xl font-bold text-slate-950">{page.title}</h1>
          <p className="mt-3 max-w-3xl text-base leading-7 text-slate-600">{page.intro}</p>
        </header>

        <div className="mt-8 space-y-6">
          {page.sections.map(section => (
            <section key={section.heading}>
              <h2 className="text-lg font-semibold text-slate-950">{section.heading}</h2>
              <p className="mt-2 text-sm leading-7 text-slate-600">{section.body}</p>
            </section>
          ))}
        </div>
      </article>
    </div>
  )
}

export default StaticPage
